/**
 * Route copy for `src/routes/listening` (keys: `routes.listening.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  addLeadToCrm: {
    createdCreated: '{{created}} created',
    existingAlreadyPresent: '{{existing}} already present',
    groupedGrouped: '{{grouped}} grouped',
    addedToTheCrmV: 'Added to the CRM — {{v}}',
    addingToTheCrmFailed: 'Adding to the CRM failed.',
    addedToTheCrm: '✓ Added to the CRM',
    addToTheCrm: 'Add to the CRM',
    targetGroup: 'Target group',
    noGroup: 'No group',
    newGroup: '➕ New group…',
    newGroupName: 'New group name',
    adding: 'Adding…',
    confirm: 'Confirm',
    cancel: 'Cancel',
  },
  engagementBar: {
    reply: 'Reply',
    like: 'Like',
    comment: 'Comment',
    hide: 'Hide',
    privateMessage: 'Private message',
    actionLabelSent: '{{ACTION_LABEL}} — sent.',
    yourV: 'Your {{v}}…',
    textActionLabel: 'Text — {{ACTION_LABEL}}',
    sending: 'Sending…',
    send: 'Send',
    draft: 'Draft…',
    aiDraft: '✨ AI draft',
    cancel: 'Cancel',
    sendingWithinThePlatformSLimits: 'Sending within the platform\'s limits.',
  },
  listeningDashboardPage: {
    newMentionsCount: '{{count}} new mention(s)',
    socialListeningFeature: 'Social listening',
    keyCredentialNotConfigured: 'key/credential not configured',
    noOfficialApi: 'no official API',
    notAvailableV: ' — not available{{v}}',
    mentions: 'Mentions',
    mentionVolume: 'Mention volume',
    volumeOverTheWindow: 'Volume over the window',
    netSentiment: 'Net sentiment',
    positive: 'Positive',
    neutral: 'Neutral',
    negative: 'Negative',
    topTopics: 'Top topics',
    noTopicDetectedOverTheWindow: 'No topic detected over the window.',
    noNewMentions: 'No new mentions',
    baseGapsSourceSUnavailable: '{{base}} · {{gaps}} source(s) unavailable',
    monitorCreated: 'Monitor created',
    createdButTheInitialRefreshFailed: 'Created, but the initial refresh failed.',
    theCreationFailed: 'The creation failed.',
    theRefreshFailed: 'The refresh failed.',
    failuresMonitorSCouldNotBe: '{{failures}} monitor(s) could not be refreshed.',
    vNewMentionSInTotal: '{{v}} new mention(s) in total',
    theUpdateFailed: 'The update failed.',
    monitorDeleted: 'Monitor deleted',
    deletionFailed: 'Deletion failed.',
    monitors: 'Monitors',
    refreshing: 'Refreshing…',
    refreshAll: 'Refresh all',
    name: 'Name',
    myBrand: 'My brand',
    type: 'Type',
    query: 'Query',
    termKeywordOrCompetitorAccount: 'Term, keyword or competitor @account',
    karexiaOrKarexiaCom: '"Karexia" OR karexia.com',
    creating: 'Creating…',
    add: 'Add',
    readOnlyRoleManagingMonitorsIs: 'Read-only role: managing monitors is reserved for authorized roles.',
    noMonitorsCreateOneToTrack: 'No monitors. Create one to track a brand, a keyword or a competitor.',
    active: 'Active',
    paused: 'Paused',
    refresh: 'Refresh',
    pause: 'Pause',
    enable: 'Enable',
    del: 'Del.',
    noMentions: 'No mentions',
    noMentionForTheCurrentFilters: 'No mention for the current filters. Mentions will appear here as soon as the sources report them.',
    reach: 'Reach:',
    theSourceDoesNotExposeReach: 'the source does not expose reach',
    source: 'Source ↗',
    alerts: 'Alerts',
    noRecentAlert: 'No recent alert.',
    alertTriggered: 'Alert triggered',
    couldNotLoadTheListeningData: 'Could not load the listening data.',
    monitoringListening: 'Monitoring / Listening',
    monitorYourBrandKeywordsAndCompetitors: 'Monitor your brand, keywords and competitors: mentions, sentiment and top topics, with full transparency on sources.',
    window: 'Window',
    monitor: 'Monitor',
    all: 'All',
    sentiment: 'Sentiment',
    loadingTheListeningData: 'Loading the listening data…',
  },
  listeningFeedPage: {
    socialNetworks: 'Social networks',
    web: 'Web',
    reviews: 'Reviews',
    monitoring: 'Monitoring',
    comment: 'Comment',
    hashtag: 'Hashtag',
    reaction: 'Reaction',
    positive: 'Positive',
    neutral: 'Neutral',
    negative: 'Negative',
    anyTime: 'Any time',
    last24H: 'Last 24 h',
    last7Days: 'Last 7 days',
    last30Days: 'Last 30 days',
    listening: 'Listening',
    socialNetworksTheWebNewsBlogs: 'Social networks, the web (news, blogs, forums) and customer reviews — engage, reply and convert into CRM contacts.',
    refresh: 'Refresh',
    listeningSections: 'Listening sections',
    platform: 'Platform',
    filterByPlatform: 'Filter by platform',
    allPlatforms: 'All platforms',
    type: 'Type',
    filterByType: 'Filter by type',
    allTypes: 'All types',
    period: 'Period',
    filterByPeriod: 'Filter by period',
    listeningOnYourOwnPagesAccounts: 'Listening on your own pages/accounts. Engagement (reply, like, comment, hide, DM) happens within the limits each platform allows — no out-of-policy automation, no scraping.',
    loadingTheFeed: 'Loading the feed…',
    socialListeningIsNotAvailableOn: 'Social listening is not available on this environment yet (service being deployed or account not connected). Try again later.',
    couldNotLoadTheListeningFeed: 'Could not load the listening feed.',
    tryAgain: 'Try again',
    noInteractionMatchesTheseFilters: 'No interaction matches these filters.',
    positivesharePercentPositiveInteractions: '{{positiveShare}} percent positive interactions',
    netSentiment: 'Net sentiment',
    notAssessedOnThisFeed: 'Not assessed on this feed',
    positivePositiveNegativeNegative: '{{positive}} positive · {{negative}} negative',
    interactions: 'Interactions',
    positive2: 'Positive',
    negative2: 'Negative',
    platforms: 'Platforms',
    n14DayActivity: '14-day activity',
    interactionVolumeOverTheLast14: 'Interaction volume over the last 14 days',
    negativeNegativeInteractionSToHandle: '{{negative}} negative interaction(s) to handle first.',
    mostlyPositiveSentimentPositiveshare: 'Mostly positive sentiment ({{positiveShare}}%).',
    vAccountsForV2OfInteractions: '{{v}} accounts for {{v2}}% of interactions.',
    activityPeakPeakInteractionsInA: 'Activity peak: {{peak}} interactions in a single day.',
    breakdownBySentiment: 'Breakdown by sentiment',
    sentimentNotAssessedOnThisFeed: 'Sentiment not assessed on this feed.',
    byPlatform: 'By platform',
    keyTakeaway: 'Key takeaway',
    lengthAttachmentS: '📎 {{length}} attachment(s)',
    viewOnThePlatform: 'View on the platform ↗',
  },
  reviewsTab: {
    allRatings: 'All ratings',
    n4AndUp: '4★ and up',
    n3AndUp: '3★ and up',
    n2AndUp: '2★ and up',
    n1AndUp: '1★ and up',
    allReviews: 'All reviews',
    toAnswer: 'To answer',
    answered: 'Answered',
    ratingNA: 'Rating n/a',
    ratingOutOf5: '{{rating}} out of 5',
    source: 'Source',
    allSources: 'All sources',
    note: 'Note',
    status: 'Status',
    yourCustomerReviewsGoogleYelpApp: 'Your customer reviews (Google, Yelp, App Store, Facebook…) gathered in one queue. Replies are published within the limits each platform allows.',
    loadingReviews: 'Loading reviews…',
    reviewManagementIsNotAvailableOn: 'Review management is not available on this environment yet (service being deployed or account not connected). Try again later.',
    couldNotLoadTheReviews: 'Could not load the reviews.',
    tryAgain: 'Try again',
    noReviewMatchesTheseFilters: 'No review matches these filters.',
    averageRatingAvgOutOf5: 'Average rating {{avg}} out of 5',
    averageRating: 'Average rating',
    noRating: 'No rating',
    outOfLengthRatedReviewS: 'out of {{length}} rated review(s)',
    needsreplyReviewSToAnswer: '{{needsReply}} review(s) to answer',
    everyReviewHasAReply: 'Every review has a reply',
    reviews: 'Reviews',
    ratingBreakdown: 'Rating breakdown',
    reviews14D: 'Reviews · 14 d',
    reviewVolumeOverTheLast14: 'Review volume over the last 14 days',
    replyPublished: 'Reply published.',
    viewTheOriginalReview: 'View the original review ↗',
    yourReply: 'Your reply',
    reply: '↩ Reply',
    yourReply2: 'Your reply…',
    replyToTheReview: 'Reply to the review',
    sending: 'Sending…',
    publishTheReply: 'Publish the reply',
    draft: 'Draft…',
    aiDraft: '✨ AI draft',
    cancel: 'Cancel',
  },
  surveillanceTab: {
    positive: 'Positive',
    neutral: 'Neutral',
    negative: 'Negative',
    thresholdReached: 'Threshold reached',
    abnormalSpike: 'Abnormal spike',
    spikeDetection: 'spike detection',
    winMinWindowNoThreshold: '{{win}}-min window — no threshold',
    monitorDeleted: 'Monitor deleted',
    deleteTheMonitor: 'Delete the monitor',
    deleteNameItsMatchesAndAlerts: 'Delete “{{name}}”? Its matches and alerts will no longer be attached to a monitor.',
    delete: 'Delete',
    monitorDeleted2: 'Monitor deleted.',
    monitorDisabled: 'Monitor disabled.',
    monitorEnabled: 'Monitor enabled.',
    vMonitorSEvaluatedRaisedAlert: '{{v}} monitor(s) evaluated · {{raised}} alert(s) raised.',
    vMonitorSEvaluatedNoAlerts: '{{v}} monitor(s) evaluated · no alerts.',
    keywordMonitoring: 'Keyword monitoring',
    createMonitorsBooleanQueriesEvaluatedOn: 'Create monitors (boolean queries) evaluated on every incoming interaction. Each match is recorded; thresholds and spikes trigger alerts.',
    newMonitor: '+ New monitor',
    loadingMonitors: 'Loading monitors…',
    monitoringIsNotAvailableOnThis: 'Monitoring is not available on this environment yet (service being deployed). Try again later.',
    couldNotLoadTheMonitors: 'Could not load the monitors.',
    tryAgain: 'Try again',
    noMonitorsYet: 'No monitors yet.',
    createAMonitor: '+ Create a monitor',
    anEditingRoleIsRequiredTo: 'An editing role is required to create a monitor.',
    filterTheMatchesAndAlertsOn: 'Filter the matches and alerts on this monitor',
    active: 'Active',
    inactive: 'Inactive',
    allPlatforms: 'All platforms',
    rule: 'Rule:',
    disable: 'Disable',
    enable: 'Enable',
    viewTheRecord: 'View the record',
    edit: 'Edit',
    alerts: 'Alerts',
    checking: 'Checking…',
    checkNow: 'Check now',
    filteredOn: 'Filtered on',
    showAll: 'show all',
    loadingAlerts: 'Loading alerts…',
    alertsUnavailableServiceBeingDeployed: 'Alerts unavailable (service being deployed).',
    couldNotLoadTheAlerts: 'Could not load the alerts.',
    noAlerts: 'No alerts.',
    runCheckNowToEvaluateThe: ' Run “Check now” to evaluate the thresholds.',
    matchEsOverWindowMinutesMin: 'match(es) over {{window_minutes}} min',
    thresholdThresholdval: ' (threshold {{thresholdVal}})',
    baselineBaselineval: ' (baseline {{baselineVal}})',
    recentMatches: 'Recent matches',
    total: 'Total',
    loading: 'Loading…',
    matchesUnavailableServiceBeingDeployed: 'Matches unavailable (service being deployed).',
    couldNotLoadTheMatches: 'Could not load the matches.',
    noMatchesYetActiveMonitorsRecord: 'No matches yet. Active monitors record the incoming interactions that satisfy their query.',
    theNameIsRequired: 'The name is required.',
    theQueryKeywordsIsRequired: 'The query (keywords) is required.',
    monitorUpdated: 'Monitor updated.',
    monitorCreated: 'Monitor created.',
    editTheMonitor: 'Edit the monitor',
    newMonitor2: 'New monitor',
    cancel: 'Cancel',
    saving: 'Saving…',
    save: 'Save',
    create: 'Create',
    name: 'Name',
    eGBrandMentions: 'E.g. Brand mentions',
    queryKeywords: 'Query (keywords)',
    eGCustomerServiceOrSupport: 'E.g. ("customer service" OR support) AND NOT hiring',
    booleanGrammarQuotedPhrasesSimpleTerms: 'Boolean grammar: “quoted phrases”, simple terms (whole word), operators',
    and: 'AND',
    or: 'OR',
    not: 'NOT',
    andParenthesesToGroupAndIs: ', and parentheses to group. AND is implicit between two adjacent terms.',
    platforms: 'Platforms',
    noSelectionAllPlatforms: 'No selection = all platforms.',
    alertRule: 'Alert rule',
    windowMinutes: 'Window (minutes)',
    thresholdMatches: 'Threshold (matches)',
    optional: 'Optional',
    aThresholdAlertIsRaisedWhen: 'A “threshold” alert is raised when the number of matches in the window reaches this threshold. Leave empty to not monitor a threshold.',
    alsoDetectAbnormalVolumeSpikes: 'Also detect abnormal volume spikes',
    monitorActiveEvaluatedOnIncomingInteract: 'Monitor active (evaluated on incoming interactions)',
  },
  webListeningTab: {
    positive: 'Positive',
    neutral: 'Neutral',
    negative: 'Negative',
    anyTime: 'Any time',
    last24H: 'Last 24 h',
    last7Days: 'Last 7 days',
    last30Days: 'Last 30 days',
    keywordBrand: 'Keyword / brand',
    yourBrandProductTopic: 'Your brand, product, topic…',
    source: 'Source',
    filtersTheLoadedResultsBySource: 'Filters the loaded results by source',
    allSources: 'All sources',
    period: 'Period',
    search: 'Search',
    monitoringOfTheWholeWebNews: 'Monitoring of the whole web (news, blogs, forums, review sites) based on your keywords — beyond your own social networks.',
    enterAKeywordOrABrand: 'Enter a keyword or a brand above, then run the search to show the web mentions.',
    searchingWebMentions: 'Searching web mentions…',
    webListeningConnectorNotConfigured: 'Web listening connector not configured.',
    noNewsRssSourceIsLinked: 'No news / RSS source is linked to this workspace, so no web mention can be retrieved. Configure a web listening connector (RSS feed / news search) to enable this monitoring.',
    webListeningIsNotAvailableOn: 'Web listening is not available on this environment yet (service being deployed). Try again later.',
    couldNotLoadTheWebMentions: 'Could not load the web mentions.',
    tryAgain: 'Try again',
    noWebMentionMatchesTheseFilters: 'No web mention matches these filters.',
    negativeNegativeMentionS: '{{negative}} negative mention(s)',
    positivesharePercentPositiveMentions: '{{positiveShare}} percent positive mentions',
    tone: 'Tone',
    mentions: 'Mentions',
    sources: 'Sources',
    positive2: 'Positive',
    negative2: 'Negative',
    bySource: 'By source',
    mentions14D: 'Mentions · 14 d',
    mentionVolumeOverTheLast14: 'Mention volume over the last 14 days',
    webMention: 'Web mention',
    byAuthor: 'By {{author}}',
  },
};

export default en;
